import { useMemo, useState } from "react";
import { Check, Palette, Plus, Tag as TagIcon, X } from "lucide-react";
import { toast } from "sonner";
import { Popover, PopoverContent, PopoverTrigger } from "@bookdental/ui";
import { cn } from "@/lib/cn";

type Props = {
  value: string[];
  onChange: (tags: string[]) => void;
  placeholder?: string;
  /** Sugestões personalizadas (ex: tags já usadas pela clínica). */
  suggestions?: string[];
  /** Tags pré-prontas sempre visíveis enquanto o input tá vazio. */
  presets?: string[];
};

/** Paleta — cada cor tem duas variantes: SOFT (bg claro + texto escuro pro
 *  chip padrão) e SOLID (bg vibrante saturado + texto branco pra destaque).
 *  User escolhe tanto a família quanto a intensidade. */
const TAG_PALETTE = [
  // ─── Soft / pastel (legibilidade alta, estilo Notion) ───
  { key: "red", bg: "#fde2e2", text: "#991b1b", ring: "#fca5a5" },
  { key: "rose", bg: "#ffe4e6", text: "#9f1239", ring: "#fda4af" },
  { key: "pink", bg: "#fce7f3", text: "#9d174d", ring: "#f9a8d4" },
  { key: "fuchsia", bg: "#fae8ff", text: "#86198f", ring: "#f0abfc" },
  { key: "purple", bg: "#f3e8ff", text: "#6b21a8", ring: "#d8b4fe" },
  { key: "violet", bg: "#ede9fe", text: "#5b21b6", ring: "#c4b5fd" },
  { key: "indigo", bg: "#e0e7ff", text: "#3730a3", ring: "#a5b4fc" },
  { key: "blue", bg: "#dbeafe", text: "#1e40af", ring: "#93c5fd" },
  { key: "sky", bg: "#e0f2fe", text: "#075985", ring: "#7dd3fc" },
  { key: "cyan", bg: "#cffafe", text: "#155e75", ring: "#67e8f9" },
  { key: "teal", bg: "#ccfbf1", text: "#115e59", ring: "#5eead4" },
  { key: "emerald", bg: "#d1fae5", text: "#065f46", ring: "#6ee7b7" },
  { key: "green", bg: "#dcfce7", text: "#166534", ring: "#86efac" },
  { key: "lime", bg: "#ecfccb", text: "#3f6212", ring: "#bef264" },
  { key: "yellow", bg: "#fef9c3", text: "#854d0e", ring: "#facc15" },
  { key: "amber", bg: "#fef3c7", text: "#854d0e", ring: "#fcd34d" },
  { key: "orange", bg: "#ffedd5", text: "#9a3412", ring: "#fdba74" },
  { key: "stone", bg: "#f5f5f4", text: "#44403c", ring: "#d6d3d1" },

  // ─── Vibrant / saturado (texto branco, alta energia) ───
  { key: "red-solid", bg: "#ef4444", text: "#ffffff", ring: "#dc2626" },
  { key: "rose-solid", bg: "#f43f5e", text: "#ffffff", ring: "#e11d48" },
  { key: "pink-solid", bg: "#ec4899", text: "#ffffff", ring: "#db2777" },
  { key: "fuchsia-solid", bg: "#d946ef", text: "#ffffff", ring: "#c026d3" },
  { key: "purple-solid", bg: "#a855f7", text: "#ffffff", ring: "#9333ea" },
  { key: "violet-solid", bg: "#8b5cf6", text: "#ffffff", ring: "#7c3aed" },
  { key: "indigo-solid", bg: "#6366f1", text: "#ffffff", ring: "#4f46e5" },
  { key: "blue-solid", bg: "#3b82f6", text: "#ffffff", ring: "#2563eb" },
  { key: "sky-solid", bg: "#0ea5e9", text: "#ffffff", ring: "#0284c7" },
  { key: "cyan-solid", bg: "#06b6d4", text: "#ffffff", ring: "#0891b2" },
  { key: "teal-solid", bg: "#14b8a6", text: "#ffffff", ring: "#0d9488" },
  { key: "emerald-solid", bg: "#10b981", text: "#ffffff", ring: "#059669" },
  { key: "green-solid", bg: "#22c55e", text: "#ffffff", ring: "#16a34a" },
  { key: "lime-solid", bg: "#84cc16", text: "#ffffff", ring: "#65a30d" },
  { key: "yellow-solid", bg: "#eab308", text: "#ffffff", ring: "#ca8a04" },
  { key: "amber-solid", bg: "#f59e0b", text: "#ffffff", ring: "#d97706" },
  { key: "orange-solid", bg: "#f97316", text: "#ffffff", ring: "#ea580c" },
  { key: "zinc-solid", bg: "#52525b", text: "#ffffff", ring: "#3f3f46" },
] as const;

type PaletteColor = (typeof TAG_PALETTE)[number];

const SOFT_COLORS = TAG_PALETTE.filter(
  (c) => !c.key.endsWith("-solid"),
);
const SOLID_COLORS = TAG_PALETTE.filter((c) => c.key.endsWith("-solid"));

const LS_CUSTOM_COLORS = "bd:tag-colors:v1";

/** Normaliza: lowercase + sem acentos + trim — pra comparar duplicatas. */
function normalizeTag(s: string): string {
  return s
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase()
    .trim();
}

/** Capitaliza só a primeira letra (o resto do que o user digitou é preservado). */
function capitalizeFirst(s: string): string {
  if (!s) return s;
  return s.charAt(0).toUpperCase() + s.slice(1);
}

function hashIndex(name: string): number {
  const norm = normalizeTag(name);
  let h = 0;
  for (let i = 0; i < norm.length; i++) h = (h << 5) - h + norm.charCodeAt(i);
  return Math.abs(h) % TAG_PALETTE.length;
}

/** Lê custom colors persistidos no localStorage. Falhas silenciosas. */
function readCustomColors(): Record<string, string> {
  if (typeof window === "undefined") return {};
  try {
    const raw = localStorage.getItem(LS_CUSTOM_COLORS);
    if (!raw) return {};
    const parsed = JSON.parse(raw);
    return typeof parsed === "object" && parsed !== null ? parsed : {};
  } catch {
    return {};
  }
}

function writeCustomColors(map: Record<string, string>): void {
  if (typeof window === "undefined") return;
  try {
    localStorage.setItem(LS_CUSTOM_COLORS, JSON.stringify(map));
  } catch {
    // noop — quota cheia ou localStorage disabled
  }
}

/** Retorna a cor configurada pra tag. Se user não salvou custom, usa hash. */
export function tagColorFor(name: string): PaletteColor {
  const norm = normalizeTag(name);
  const custom = readCustomColors()[norm];
  if (custom) {
    const found = TAG_PALETTE.find((c) => c.key === custom);
    if (found) return found;
  }
  return TAG_PALETTE[hashIndex(name)];
}

/** Tag input com chips coloridos, duplicate check + criador de tag com cor. */
export function PatientTagInput({
  value,
  onChange,
  placeholder = "Nenhuma tag selecionada",
  suggestions = [],
  presets = [],
}: Props) {
  // Força re-render quando custom colors mudam via Popover
  const [colorTick, setColorTick] = useState(0);

  const add = (raw: string) => {
    const t = capitalizeFirst(raw.trim());
    if (!t || t.length > 50 || value.length >= 20) return;

    // Duplicate check insensitive
    const normNew = normalizeTag(t);
    const existing = value.find((x) => normalizeTag(x) === normNew);
    if (existing) {
      toast.warning(
        existing === t
          ? `Tag "${t}" já existe`
          : `Tag similar já existe: "${existing}"`,
        { duration: 2500 },
      );
      return;
    }

    onChange([...value, t]);
  };

  const remove = (t: string) => onChange(value.filter((x) => x !== t));

  const availablePool = useMemo(() => {
    const pool = Array.from(new Set([...presets, ...suggestions]));
    return pool.filter(
      (s) => !value.some((v) => normalizeTag(v) === normalizeTag(s)),
    );
    // colorTick é apenas pra invalidar — não afeta conteúdo
  }, [presets, suggestions, value]);

  return (
    <div className="space-y-3" data-color-tick={colorTick}>
      {/* ─── 1. Chips selecionados ──────────────────────── */}
      <div
        className={cn(
          "flex min-h-[40px] flex-wrap items-center gap-1.5 rounded-md border border-dashed border-zinc-200 bg-zinc-50/40 p-2 dark:border-border dark:bg-muted/20",
          value.length === 0 && "justify-center",
        )}
      >
        {value.length === 0 ? (
          <span className="text-[11px] italic text-zinc-400 dark:text-muted-foreground">
            {placeholder}
          </span>
        ) : (
          value.map((t) => {
            const c = tagColorFor(t);
            return (
              <span
                key={t}
                className="inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[11.5px] font-semibold shadow-sm ring-1"
                style={{
                  backgroundColor: c.bg,
                  color: c.text,
                  ["--tw-ring-color" as string]: c.ring,
                }}
              >
                {t}
                <button
                  type="button"
                  onClick={() => remove(t)}
                  className="rounded-full opacity-70 transition-opacity hover:opacity-100"
                  style={{ color: c.text }}
                  aria-label={`Remover tag ${t}`}
                >
                  <X className="h-3 w-3" />
                </button>
              </span>
            );
          })
        )}
      </div>

      {/* ─── 2. Tags disponíveis pra adicionar ──────────── */}
      {availablePool.length > 0 && (
        <div>
          <p className="mb-1.5 text-[10px] font-bold uppercase tracking-wider text-zinc-500 dark:text-muted-foreground">
            Tags disponíveis
          </p>
          <div className="flex flex-wrap gap-1">
            {availablePool.map((s) => {
              const c = tagColorFor(s);
              return (
                <button
                  key={s}
                  type="button"
                  onClick={() => add(s)}
                  className="inline-flex items-center gap-0.5 rounded-full px-2 py-0.5 text-[11px] font-medium ring-1 transition-all hover:-translate-y-px hover:shadow-sm"
                  style={{
                    backgroundColor: c.bg,
                    color: c.text,
                    ["--tw-ring-color" as string]: c.ring,
                  }}
                >
                  <Plus className="h-2.5 w-2.5 opacity-60" />
                  {s}
                </button>
              );
            })}
          </div>
        </div>
      )}

      {/* ─── 3. Criar tag (popover) ─────────────────────── */}
      <CreateTagPopover
        onCreate={(name, colorKey) => {
          const norm = normalizeTag(name);
          const custom = readCustomColors();
          custom[norm] = colorKey;
          writeCustomColors(custom);
          setColorTick((n) => n + 1);
          add(name);
        }}
      />
    </div>
  );
}

// ─── Popover de criação ──────────────────────────────────────

function CreateTagPopover({
  onCreate,
}: {
  onCreate: (name: string, colorKey: string) => void;
}) {
  const [open, setOpen] = useState(false);
  const [name, setName] = useState("");
  const [colorIdx, setColorIdx] = useState(0);

  const trimmed = name.trim();
  const color = TAG_PALETTE[colorIdx];
  const canCreate = trimmed.length >= 1 && trimmed.length <= 50;

  const reset = () => {
    setName("");
    setColorIdx(0);
  };

  const handleCreate = () => {
    if (!canCreate) return;
    onCreate(trimmed, color.key);
    reset();
    setOpen(false);
  };

  // Auto-seleciona cor por hash do nome enquanto user digita (até ele mexer)
  const [colorTouched, setColorTouched] = useState(false);
  const handleNameChange = (raw: string) => {
    const capitalized = capitalizeFirst(raw);
    setName(capitalized);
    if (!colorTouched && capitalized.trim()) {
      setColorIdx(hashIndex(capitalized));
    }
  };

  return (
    <Popover
      open={open}
      onOpenChange={(o) => {
        setOpen(o);
        if (!o) {
          reset();
          setColorTouched(false);
        }
      }}
    >
      <PopoverTrigger asChild>
        <button
          type="button"
          className="inline-flex h-8 items-center gap-1.5 rounded-md border border-dashed border-brand-300 bg-brand-50/40 px-3 text-[12px] font-semibold text-brand-700 transition-all hover:border-brand-500 hover:bg-brand-50 hover:text-brand-800 dark:border-primary/40 dark:bg-primary/5 dark:text-primary dark:hover:border-primary dark:hover:bg-primary/10"
        >
          <Plus className="h-3.5 w-3.5" />
          Criar tag
        </button>
      </PopoverTrigger>
      <PopoverContent
        align="start"
        sideOffset={8}
        className="w-[300px] overflow-hidden border border-zinc-200 bg-white p-0 shadow-xl dark:border-border dark:bg-card"
      >
        <div className="space-y-3 p-4">
          <div className="flex items-center gap-2">
            <span
              className="flex h-7 w-7 items-center justify-center rounded-md bg-brand-100 text-brand-700 dark:bg-primary/15 dark:text-primary"
            >
              <TagIcon className="h-3.5 w-3.5" />
            </span>
            <div>
              <h4 className="text-[12.5px] font-bold tracking-tight text-zinc-900 dark:text-foreground">
                Nova tag
              </h4>
              <p className="text-[10.5px] text-zinc-500 dark:text-muted-foreground">
                Nome e cor personalizadas.
              </p>
            </div>
          </div>

          {/* Nome */}
          <label className="block">
            <span className="mb-1 block text-[10px] font-bold uppercase tracking-wider text-zinc-500 dark:text-muted-foreground">
              Nome
            </span>
            <input
              autoFocus
              value={name}
              onChange={(e) => handleNameChange(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  e.preventDefault();
                  handleCreate();
                }
              }}
              maxLength={50}
              placeholder="Ex.: VIP, Alérgico..."
              className="h-9 w-full rounded-md border border-zinc-200 bg-white px-2.5 text-sm text-zinc-900 outline-none placeholder:text-zinc-400 focus:border-brand-400 focus:ring-1 focus:ring-brand-400 dark:border-border dark:bg-background dark:text-foreground dark:placeholder:text-muted-foreground"
            />
          </label>

          {/* Paleta — suaves + vibrantes */}
          <div>
            <div className="mb-1.5 flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-wider text-zinc-500 dark:text-muted-foreground">
              <Palette className="h-3 w-3" />
              Cor
            </div>
            <div className="space-y-2">
              <PaletteGrid
                label="Suaves"
                colors={SOFT_COLORS}
                activeKey={color.key}
                onPick={(key) => {
                  const idx = TAG_PALETTE.findIndex((c) => c.key === key);
                  if (idx >= 0) {
                    setColorIdx(idx);
                    setColorTouched(true);
                  }
                }}
              />
              <PaletteGrid
                label="Vibrantes"
                colors={SOLID_COLORS}
                activeKey={color.key}
                onPick={(key) => {
                  const idx = TAG_PALETTE.findIndex((c) => c.key === key);
                  if (idx >= 0) {
                    setColorIdx(idx);
                    setColorTouched(true);
                  }
                }}
              />
            </div>
          </div>

          {/* Preview */}
          {trimmed && (
            <div>
              <p className="mb-1 text-[10px] font-bold uppercase tracking-wider text-zinc-500 dark:text-muted-foreground">
                Preview
              </p>
              <span
                className="inline-flex items-center gap-1 rounded-full px-2.5 py-1 text-[12px] font-semibold shadow-sm ring-1"
                style={{
                  backgroundColor: color.bg,
                  color: color.text,
                  ["--tw-ring-color" as string]: color.ring,
                }}
              >
                {trimmed}
              </span>
            </div>
          )}

          {/* Ações */}
          <div className="flex items-center justify-end gap-1.5 border-t border-zinc-100 pt-3 dark:border-border">
            <button
              type="button"
              onClick={() => {
                reset();
                setOpen(false);
              }}
              className="h-8 rounded-md border border-zinc-200 px-3 text-[12px] font-medium text-zinc-700 transition-colors hover:bg-zinc-50 dark:border-border dark:text-foreground dark:hover:bg-muted"
            >
              Cancelar
            </button>
            <button
              type="button"
              onClick={handleCreate}
              disabled={!canCreate}
              className="inline-flex h-8 items-center gap-1 rounded-md bg-brand-600 px-3 text-[12px] font-semibold text-white shadow-sm transition-colors hover:bg-brand-700 disabled:opacity-50"
            >
              <Check className="h-3.5 w-3.5" />
              Criar tag
            </button>
          </div>
        </div>
      </PopoverContent>
    </Popover>
  );
}

function PaletteGrid({
  label,
  colors,
  activeKey,
  onPick,
}: {
  label: string;
  colors: readonly PaletteColor[];
  activeKey: string;
  onPick: (key: string) => void;
}) {
  return (
    <div>
      <p className="mb-1 text-[9px] font-semibold uppercase tracking-wider text-zinc-400 dark:text-muted-foreground/80">
        {label}
      </p>
      <div className="grid grid-cols-9 gap-1">
        {colors.map((c) => {
          const active = activeKey === c.key;
          return (
            <button
              key={c.key}
              type="button"
              onClick={() => onPick(c.key)}
              className={cn(
                "relative flex h-6 items-center justify-center rounded-md transition-all hover:scale-110",
                active
                  ? "ring-2 ring-offset-1 ring-offset-white dark:ring-offset-card"
                  : "ring-1 ring-transparent",
              )}
              style={{
                backgroundColor: c.bg,
                ["--tw-ring-color" as string]: active ? c.text : c.ring,
              }}
              aria-label={`Cor ${c.key}`}
              title={c.key}
            >
              {active && (
                <Check className="h-3 w-3" style={{ color: c.text }} />
              )}
            </button>
          );
        })}
      </div>
    </div>
  );
}
